import React, { createContext, useContext, useState } from "react";
import { Alert, IconButton, Snackbar } from "@mui/material";
import { Close } from "@mui/icons-material";

const SnackbarContext = createContext();

export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error("useSnackbar must be used within a SnackbarProvider");
  }
  return context;
};

export const SnackbarProvider = ({ children }) => {
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showMessage = (message, severity = null) => {
    // Auto-detect severity if not provided
    if (!severity) {
      if (
        message.toLowerCase().includes("error") ||
        message.toLowerCase().includes("failed")
      ) {
        severity = "error";
      } else if (
        message.toLowerCase().includes("warning") ||
        message.toLowerCase().includes("warn")
      ) {
        severity = "warning";
      } else if (message.toLowerCase().includes("info")) {
        severity = "info";
      } else {
        severity = "success";
      }
    }

    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const showSuccess = (message) => {
    showMessage(message, "success");
  };

  const showError = (message) => {
    showMessage(message, "error");
  };

  const showWarning = (message) => {
    showMessage(message, "warning");
  };

  const showInfo = (message) => {
    showMessage(message, "info");
  };

  const close = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    close();
  };

  const value = {
    showMessage,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    close,
    handleClose,
  };

  return (
    <SnackbarContext.Provider value={value}>
      {children}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleClose}
        message={snackbar.message}
        action={
          <React.Fragment>
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={handleClose}
            >
              <Close fontSize="small" />
            </IconButton>
          </React.Fragment>
        }
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
};
